<script module>
  /**
   * @typedef { Object } MapToolProps
   * @property { Array<Record<string, any>> } data
   * @property { Array<Record<string, any>> } [tableData]
   * @property { Array<Record<string, any>> } configData
   * @property { import("$lib/types").mapType } [mapType="geo"]
   * @property { string } [accentColor = "#Fdbf11"]
   * @property {import("$utils/states").FeatureOptions} [featureOption="states"]
   * @property { string } [showStateSummary]
   * @property { string } dropdownLabel
   * @property { string } tablePlaceholderLabel
   */
</script>

<script>
  import MapComponent from "$lib/components/Map/Map.svelte";
  import DetailHeader from "$lib/components/DetailHeader.svelte";
  import { Block, BasicDropdown, Theme } from "@urbaninstitute/dataviz-components";
  import windowWidth from "$lib/stores/windowWidth.js";
  import { createConfig, setConfig } from "$lib/utils/config.js";
  import { getStatesInfo, getStateName } from "$utils/states.js";
  import { ToolData } from "$utils/data.js";
  import InfoBox from "./InfoBox.svelte";
  import InfoTable from "./InfoTable.svelte";

  /**
   * @typedef { Object } MapToolProps
   * @property { Array<Record<string, any>> } data
   * @property { Array<Record<string, any>> } [tableData]
   * @property { Array<Record<string, any>> } [questions_data]
   * @property { Array<Record<string, any>> } configData
   * @property { import("$lib/types").mapType } [mapType="geo"]
   * @property { string } [accentColor = "#Fdbf11"]
   * @property {import("$utils/states").FeatureOptions} [featureOption="states"]
   * @property { string } [showStateSummary]
   * @property { string } dropdownLabel
   * @property { string } tablePlaceholderLabel
   * @property { boolean } [hideDropdown = false]
   */

  /** @type {MapToolProps} */
  const {
    data,
    tableData,
    questions_data,
    configData,
    mapType = "geo",
    showStateSummary,
    accentColor = "#fdbf11",
    featureOption = "states",
    dropdownLabel = "Select a metric to view",
    tablePlaceholderLabel = "Select a state or district to see data by county",
    hideDropdown = false
  } = $props();

  // use this effect to load data from the URLs passed as properties
  $effect(() => {
    // fetch primary data and store in $state
    if (!parsedData) {
      parsedData = new ToolData(data, tableData, configData);
    }
    // fetch config data and store in $state
    if (!parsedConfigData) {
      /** @satisfies { import("$utils/config").ColumnConfig[]} */
      parsedConfigData = createConfig(configData, accentColor);
    }
  });

  // hard coded values for this instance
  const MAP_COLUMNS = ["status"];
  const TOOLTIP_COLUMNS = ["status"];

  /**
   * @param {Record<string, any>[]} allData
   * @param {string} map_id
   * @returns {Record<string, any>[]}
   */
  function getTableData(allData, map_id) {
    return allData.filter((d) => d.map_id === map_id);
  }

  let currentState = $state();
  let currentMetric = $state();
  // use effect to update current Metric to first data column if it isn't defined
  $effect(() => {
    if (parsedData && !currentMetric) {
      currentMetric = parsedData.getDataColumns()[0];
    }
  });

  let question_bank = $derived(questions_data 
                                  ? questions_data
                                    .reduce((acc, d) => {
                                      acc[d.question_id] = {question: d.question, explanation: d.explanation, category: d.category_name};
                                      return acc;
                                    }, {})
                                  : [])
  $inspect(question_bank)

  /**
   * State to hold primary data
   * @type {ToolData | undefined}
   */
  let parsedData = $state();
  // state to hold configuration data
  let parsedConfigData = $state();
  // is the tool using the primary data for the table?
  let tableUsesMainData = $state(true);

  // set the config data into the context so child components can access it without prop drilling
  setConfig(() => parsedConfigData);

  // parse state summary into boolean
  const shouldShowStateSummary = $derived(showStateSummary === "true" && !tableUsesMainData);

  // derived state to hold the current map configuration, which is set based on the metric selection
  let currentMapConfig = $derived(
    currentMetric && parsedConfigData ? parsedConfigData.columns.get(currentMetric) : undefined
  );

  const stateDropdownData = $derived(
    currentMetric && parsedConfigData ? getStatesInfo(featureOption).map(({ fips, state }) => ({
    value: fips,
    label: state
  })).filter(d=> parsedData?.getRow(d.value).status.includes("Active")) : [])

  const currentStateData = $derived(parsedData ? parsedData.getRow(currentState) : undefined);
  const currentStateVariables = $derived(getStateVariables(currentStateData));

  // build dropdown options from colNames
  const dataMetrics = $derived(
    parsedData && parsedConfigData
      ? parsedData
          .getDataColumns()
          .filter((k) => MAP_COLUMNS.includes(k)) // manual override
          .map((k) => ({ value: k, label: parsedConfigData.columns.get(k).column_label }))
      : []
  );


  let licensingTableData = $derived(
    currentStateData
      ? Object.keys(currentStateData)
      .filter(d => d.includes("classification_and_licensing"))
      .map(key => ({
            Question: `${question_bank[key].question}`,
            Explanation: `${question_bank[key].explanation}`,
            Answer: `${currentStateData[key]}`
      }))
      : []
    );

    let pricingAndFeesTable = $derived(
    currentStateData
      ? Object.keys(currentStateData)
      .filter(d => d.includes("pricing_and_fees"))
      .map(key => ({
            Question: `${question_bank[key].question}`,
            Explanation: `${question_bank[key].explanation}`,
            Answer: `${currentStateData[key]}`
      }))
      : []
    );

    let productReqsTable = $derived(
    currentStateData
      ? Object.keys(currentStateData)
      .filter(d => d.includes("product_requirements"))
      .map(key => ({
            Question: `${question_bank[key].question}`,
            Explanation: `${question_bank[key].explanation}`,
            Answer: `${currentStateData[key]}`
      }))
      : []
    );

    let oversightAndAcc = $derived( 
    currentStateData
      ? Object.keys(currentStateData)
      .filter(d => d.includes("oversight_and_accountability"))
      .map(key => ({
            Question: `${question_bank[key].question}`,
            Explanation: `${question_bank[key].explanation}`,
            Answer: `${currentStateData[key]}`
      }))
      : []
    );

  let allTablesData = $derived([licensingTableData,pricingAndFeesTable,productReqsTable,oversightAndAcc])
  let allTableTitles = ["Classification and Licensing","Pricing and Fees","Product Requirements","Oversight and Accountability"]


  /**
   * @param {CustomEvent<{props: {fips: string}}>} e
   */
  function handleStateSelect(e) {
    if (!e) {
      currentState = null;
      return;
    }
    if (e.detail) {
      const { fips, map_id } = e.detail.props;
      if (parsedData.getRow(map_id).status.includes("Active"))
        currentState = map_id;
    } else {
      currentState = null;
    }
  }

  /**
   * @param {string} metric
   * @param {number} maxWidth
   */
  function getDropdownWidth(metric, maxWidth) {
    return Math.min(220, maxWidth);
  }
  function getStateVariables(stateData) {
    if (!parsedConfigData) {
      return undefined;
    }
    let result = {
      state: getStateName(stateData.map_id)
    };
    for (let key in stateData) {
      let colConfig = parsedConfigData.columns.get(key);
      if (colConfig) {
        result[key] = colConfig.format(stateData[key]);
      } else {
        result[key] = stateData[key];
      }
    }
    return result;
  }

  /**
   * @param {number} metric
   * @returns {string}
   */
  function addOrdinal(metric) {
    if (metric % 10 == 1 && metric != 11) return metric + "st";
    if (metric % 10 == 2 && metric != 12) return metric + "nd";
    if (metric % 10 == 3 && metric != 13) return metric + "rd";
    return metric + "th";
  }
</script>

<svelte:window bind:innerWidth={$windowWidth} />
<Theme>
  <Block width="body">
    <!-- <h4 class="tool-title">Are EWA policies active in your state?</h4> -->
  
    {#if parsedData && parsedConfigData}
      {#if !hideDropdown}
        <div class="tool-header" style={`--accent-color: ${accentColor};`}>
          {dropdownLabel}
          <BasicDropdown
            data={dataMetrics}
            variant="secondary-yellow"
            inlineLabel="Select a metric to view"
            id="metric-select"
            placeholder={null}
            on:change={(e) => {
              // logClickToGA(e.target, "metric-select--" + e.target.value);
            }}
            bind:value={currentMetric}
          />
        </div>
      {/if}
      {#if currentMapConfig}
        <MapComponent
          data={parsedData.data}
          metric={currentMetric}
          onStateSelect={handleStateSelect}
          colorPalette={currentMapConfig.scale_color}
          scaleBreaks={currentMapConfig.scale_breaks}
          scaleType={currentMapConfig.scale_type}
          minMax={currentMapConfig.scale_min_max}
          {featureOption}
          {mapType}
          bind:currentState
          tooltipColumns={TOOLTIP_COLUMNS}
        />
      {/if}
      {/if}
</Block>
  <Block width="wide">
  {#if parsedData && parsedConfigData}
      <DetailHeader bind:currentState {stateDropdownData} label={tablePlaceholderLabel} />

      <!-- <Table data={currentTableData} cols={tableColNames} labelColumn={!tableUsesMainData} /> -->
{#if currentState}
      <div class="box">
          <h4>Overview</h4>
          <p style="margin-top: 0;">{@html currentStateData.state_intro}</p>
      </div>
      {/if}
      {#each [0,1,2,3] as index}
      <div class="box">
          <h4>
            {allTableTitles[index]}{currentState ? " in " : ""}<span class="state-name"
              >{currentState ? getStateName(currentState) : ""}</span
            >
          </h4>
          {#if currentState}
            <!-- <InfoTable data={allTablesData[index]}/> -->
            {#each allTablesData[index] as row}
            {@const first_letters = row['Answer'].split(",")[0]}
            {@const contains_context = row['Answer'].split(",").length > 1}
            <div class="one-question-answer">
              <p class="question">{@html row['Question']}</p>
              <p class ="explanation">{@html row['Explanation']}</p>
              <div class="answer">
                <p class="yes-no">{first_letters}</p>
                {#if contains_context}
                  <p class="context">{row['Answer'].split(",").slice(1).join(",")}</p>
                {/if}
            </div>
            </div>
            {/each}
          {/if}
      </div>
      {/each}
    {/if}
  </Block>
</Theme>

<style>
  :global(.tile-map .tile-map-wrap .tile-shape[fill = "#E3E3E3"]){
    cursor:not-allowed;
  }

  h4.tool-title {
    margin-bottom: var(--spacing-12) !important;
    font-size: var(--font-size-2xl) !important;
  }
  .tool-header {
    font-size: var(--font-size-2xl);
    font-weight: var(--font-weight-bold);
    margin-bottom: var(--spacing-8);
    text-align: center;
  }
  .tool-header :global(.dropdown-parent) {
    display: inline-block;
  }
  .tool-header :global(.dropdown-container .icons svg) {
    display: block;
  }
  .tool-header :global(.dropdown-container select.dropdown-select) {
    background-color: var(--accent-color);
    border: var(--accent-color);
  }
  .tool-header :global(select) {
    font-size: var(--font-size-2xl) !important;
  }
  .tool-header :global(select option) {
    font-size: var(--font-size-normal) !important;
  }
  .state-name {
    border-bottom: solid var(--color-yellow) 4px;
    /* text-underline-offset: 4px; */
  }

  .box {
    margin-bottom: var(--spacing-4);
    padding: 0 var(--spacing-8) var(--spacing-4) var(--spacing-8);
    background: var(--color-gray-shade-lightest);
  }

  .box h4 {
    margin: 0;
    position: sticky;
    padding: var(--spacing-8) 0 var(--spacing-6);
    top: 0px;
    background-color: var(--color-gray-shade-lightest);
  }

  .box .one-question-answer{
    padding: var(--spacing-2) 0 var(--spacing-8) 0;
    /* border-bottom:  2px solid var(--color-gray); */
  }

  .box .question{
    font-size: var(--font-size-large);
    line-height: var(--line-height-normal);
    font-weight: var(--font-weight-bold);
    margin: 0;
  }
  .box .explanation{
    font-size: var(--font-size-small);
    line-height: var(--line-height-snug);
    font-style: italic;
    margin-top: var(--spacing-1);
  }

  .box .answer{
    border-left: 3px solid var(--color-green-shade-light);
    margin-left: var(--spacing-2);
    padding-left: var(--spacing-2);
    /* padding: var(--spacing-2) 0 var(--spacing-2) var(--spacing-3); */
    margin-top: var(--spacing-4);
    
    .yes-no{
      margin: 0;
      text-transform: uppercase;
      font-weight: var(--font-weight-bold);
      font-size: var(--font-size-normal);
    }
    .context{
      margin: 0;
      padding: 0 0 var(--spacing-1) 0;
      font-size: var(--font-size-normal);
      line-height: var(--line-height-snug);
    }
  }
  @media (max-width: 540px) {
    .tool-header :global(select) {
      font-size: var(--font-size-xl) !important;
    }
    .tool-header {
      font-size: var(--font-size-xl) !important;
    }
  }
</style>
